import type { CSSProperties } from "react";

import type { LegacyTournament } from "../api/legacyTypes";
import { gameModule } from "../games/registry";
import { usePlayerSummaries } from "../hooks/usePlayerSummaries";
import { useTournamentRefresh } from "../hooks/useTournamentRefresh";
import { feedMatches } from "../utils/feedWindow";
import { GameBadge } from "./GameBadge";
import { MatchCard } from "./MatchCard";
import { QuickResults } from "./QuickResults";
import { LocalDateRange } from "./LocalDateTime";
import { TournamentFollowButton } from "./TournamentFollowButton";

export function TournamentCard({ tournament, compact = false }: { tournament: LegacyTournament; compact?: boolean }) {
  const game = gameModule(tournament.wiki);
  const playerSummaries = usePlayerSummaries(tournament.wiki, tournament.matches).data ?? {};
  const refresh = useTournamentRefresh(tournament.id);
  const upcoming = feedMatches(tournament.matches);
  const tournamentURL = `/tournaments/${tournament.wiki}/${tournament.id.split("/").at(-1)}`;
  return (
    <section
      className="tournament-card"
      data-game={tournament.wiki}
      style={{ "--game-accent": game?.accent ?? "var(--fp-accent)" } as CSSProperties}
    >
      <header>
        <div>
          <div className="tournament-badges">
            <GameBadge gameId={tournament.wiki} />
            {tournament.tier && <span className="tier-badge">Tier {tournament.tier}</span>}
          </div>
          <p><LocalDateRange start={tournament.startDate} end={tournament.endDate} /></p>
          <h2><a className="tournament-title-link" href={tournamentURL}>{tournament.name}</a></h2>
        </div>
        <TournamentFollowButton tournamentId={tournament.id} following={tournament.interested} />
      </header>
      <div className="tournament-facts">
        <span>{tournament.prizePool || "Prize pool not listed"}</span>
        <span>{tournament.matches.length} matches</span>
        <span>{tournament.catalogueStatus || "followed"}</span>
      </div>
      <div className="tournament-actions">
        {!compact && <QuickResults tournamentId={tournament.id} gameId={tournament.wiki} matches={tournament.matches} />}
        {!compact && tournament.catalogueStatus === "ongoing" && <button className="button-secondary tournament-refresh" type="button" disabled={refresh.isPending} onClick={() => refresh.mutate()}>{refresh.isPending ? "Refreshing matches…" : refresh.isSuccess ? "Matches updated ✓" : refresh.isError ? "Refresh failed · Retry" : "Refresh matches"}</button>}
        <a className="button-primary" href={tournamentURL}>Open tournament</a>
      </div>
      {!compact && upcoming.length > 0 && (
        <div className="match-grid">
          {upcoming.map((match) => <MatchCard key={match.sourceKey} match={match} gameId={tournament.wiki} tournamentId={tournament.id} allMatches={tournament.matches} playerSummaries={playerSummaries} />)}
        </div>
      )}
    </section>
  );
}
